import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AttrezzatureActions } from './attrezzature.actions';
import { AttrezzaturePort } from '../../core/services/attrezzature/attrezzatture.port';
import { LoadingService } from '../../core/services/loading/loading.service';
import { catchError, map, of, switchMap, tap, finalize, withLatestFrom } from 'rxjs';
import { SwalService } from '../../core/services/alerts/swal.service';
import { selectLastQuery, selectPageSize } from './attrezzature.selectors';

@Injectable()
export class AttrezzatureEffects {
  private actions$ = inject(Actions);
  private api = inject(AttrezzaturePort);
  private store = inject(Store);
  private loading = inject(LoadingService);
  private swalService = inject(SwalService);

  loadList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttrezzatureActions.loadList),
      withLatestFrom(this.store.select(selectPageSize)),
      switchMap(([{ params }, pageSize]) => {
        const queryParams = {
          ...params,
          size: params?.size ?? pageSize,
          page: params?.page ?? 0,
        };
        return this.api.getList(queryParams).pipe(
          map(response =>
            AttrezzatureActions.loadListSuccess({
              items: response.content,
              totalElements: response.totalElements,
              totalPages: response.totalPages,
              pageIndex: response.pageIndex,
            })
          ),
          catchError(err =>
            of(AttrezzatureActions.loadListFailure({ error: err?.message ?? 'Errore caricamento' }))
          ),
        );
      })
    )
  );

  loadDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttrezzatureActions.loadDetails),
      tap(() => this.loading.show()),
      switchMap(({ id }) =>
        this.api.getById(id).pipe(
          map(item => AttrezzatureActions.loadDetailsSuccess({ item })),
          catchError(err =>
            of(AttrezzatureActions.loadDetailsFailure({ error: err?.message ?? 'Errore dettagli' }))
          ),
          finalize(() => this.loading.hide()),
        )
      )
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttrezzatureActions.create),
      tap(() => this.loading.show()),
      switchMap(({ dto }) =>
        this.api.create(dto).pipe(
          map(id => AttrezzatureActions.createSuccess({ id })),
          catchError(err =>
            of(AttrezzatureActions.createFailure({ error: err?.message ?? 'Errore creazione' }))
          ),
          finalize(() => this.loading.hide()),
        )
      )
    )
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttrezzatureActions.update),
      tap(() => this.loading.show()),
      switchMap(({ id, dto }) =>
        this.api.update(id, dto).pipe(
          map(resId => AttrezzatureActions.updateSuccess({ id: Number(resId ?? id) })),
          catchError(err =>
            of(AttrezzatureActions.updateFailure({ error: err?.message ?? 'Errore modifica' }))
          ),
          finalize(() => this.loading.hide()),
        )
      )
    )
  );

  setAvailable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttrezzatureActions.setAvailable),
      tap(() => this.loading.show()),
      switchMap(({ id }) =>
        this.api.setAvailable(id).pipe(
          map(resId => AttrezzatureActions.setAvailableSuccess({ id: Number(resId ?? id) })),
          catchError(err =>
            of(AttrezzatureActions.setAvailableFailure({ error: err?.message ?? 'Errore' }))
          ),
          finalize(() => this.loading.hide()),
        )
      )
    )
  );

  setAvailability$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttrezzatureActions.setAvailability),
      tap(() => this.loading.show()),
      switchMap(({ id, dto }) =>
        this.api.setAvailability(id, dto).pipe(
          map(resId => AttrezzatureActions.setAvailabilitySuccess({ id: Number(resId ?? id) })),
          catchError(err =>
            of(AttrezzatureActions.setAvailabilityFailure({ error: err?.message ?? 'Errore' }))
          ),
          finalize(() => this.loading.hide()),
        )
      )
    )
  );

  associateToCantiere$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttrezzatureActions.associateToCantiere),
      tap(() => this.loading.show()),
      switchMap(({ id, idCantiere }) =>
        this.api.associateToCantiere(id, idCantiere).pipe(
          map(resId => AttrezzatureActions.associateToCantiereSuccess({ id: Number(resId ?? id) })),
          catchError(err =>
            of(AttrezzatureActions.associateToCantiereFailure({ error: err?.message ?? 'Errore associazione cantiere' }))
          ),
          finalize(() => this.loading.hide()),
        )
      )
    )
  );

  endCantiereAssociation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttrezzatureActions.endCantiereAssociation),
      tap(() => this.loading.show()),
      switchMap(({ id }) =>
        this.api.endCantiereAssociation(id).pipe(
          map(resId => AttrezzatureActions.endCantiereAssociationSuccess({ id: Number(resId ?? id) })),
          catchError(err =>
            of(AttrezzatureActions.endCantiereAssociationFailure({ error: err?.message ?? 'Errore rimozione cantiere' }))
          ),
          finalize(() => this.loading.hide()),
        )
      )
    )
  );

  associateToFornitore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttrezzatureActions.associateToFornitore),
      tap(() => this.loading.show()),
      switchMap(({ id, idFornitore, dataUscita }) =>
        this.api.associateToFornitore(id, idFornitore, dataUscita).pipe(
          map(resId => AttrezzatureActions.associateToFornitoreSuccess({ id: Number(resId ?? id) })),
          catchError(err =>
            of(AttrezzatureActions.associateToFornitoreFailure({ error: err?.message ?? 'Errore associazione fornitore' }))
          ),
          finalize(() => this.loading.hide()),
        )
      )
    )
  );

  endFornitoreAssociation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttrezzatureActions.endFornitoreAssociation),
      tap(() => this.loading.show()),
      switchMap(({ id, dataRitorno }) =>
        this.api.endFornitoreAssociation(id, dataRitorno).pipe(
          map(resId => AttrezzatureActions.endFornitoreAssociationSuccess({ id: Number(resId ?? id) })),
          catchError(err =>
            of(AttrezzatureActions.endFornitoreAssociationFailure({ error: err?.message ?? 'Errore rimozione fornitore' }))
          ),
          finalize(() => this.loading.hide()),
        )
      )
    )
  );

  archive$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttrezzatureActions.archive),
      tap(() => this.loading.show()),
      switchMap(({ id, archivia, dataRitorno }) =>
        this.api.archive(id, archivia, dataRitorno).pipe(
          map(resId => AttrezzatureActions.archiveSuccess({ id: Number(resId ?? id) })),
          catchError(err =>
            of(AttrezzatureActions.archiveFailure({ error: err?.message ?? 'Errore archiviazione' }))
          ),
          finalize(() => this.loading.hide()),
        )
      )
    )
  );

  loadStoricoAssociazioni$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttrezzatureActions.loadStoricoAssociazioni),
      switchMap(({ params }) =>
        this.api.getStoricoAssociazioni(params).pipe(
          map(response =>
            AttrezzatureActions.loadStoricoAssociazioniSuccess({
              items: response.content,
              totalElements: response.totalElements,
              totalPages: response.totalPages,
              pageIndex: response.pageIndex,
            })
          ),
          catchError(err =>
            of(AttrezzatureActions.loadStoricoAssociazioniFailure({ error: err?.message ?? 'Errore storico' }))
          ),
        )
      )
    )
  );

  refreshListAfterMutations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AttrezzatureActions.createSuccess,
        AttrezzatureActions.updateSuccess,
        AttrezzatureActions.setAvailableSuccess,
        AttrezzatureActions.setAvailabilitySuccess,
        AttrezzatureActions.associateToCantiereSuccess,
        AttrezzatureActions.endCantiereAssociationSuccess,
        AttrezzatureActions.associateToFornitoreSuccess,
        AttrezzatureActions.endFornitoreAssociationSuccess,
        AttrezzatureActions.archiveSuccess,
      ),
      withLatestFrom(this.store.select(selectLastQuery)),
      map(([_, lastQuery]) => AttrezzatureActions.loadList({ params: lastQuery }))
    ),
  );

  refreshDetailsAfterMutations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AttrezzatureActions.updateSuccess,
        AttrezzatureActions.setAvailableSuccess,
        AttrezzatureActions.setAvailabilitySuccess,
        AttrezzatureActions.associateToCantiereSuccess,
        AttrezzatureActions.endCantiereAssociationSuccess,
        AttrezzatureActions.associateToFornitoreSuccess,
        AttrezzatureActions.endFornitoreAssociationSuccess,
        AttrezzatureActions.archiveSuccess,
      ),
      map(({ id }) => AttrezzatureActions.loadDetails({ id }))
    )
  );

  loadStoricoAfterDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AttrezzatureActions.loadDetailsSuccess),
      map(({ item }) => AttrezzatureActions.loadStoricoAssociazioni({
        params: { idAttrezzatura: item.id, size: 20, page: 0 }
      }))
    )
  );

  showMutationSuccessToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          AttrezzatureActions.createSuccess,
          AttrezzatureActions.updateSuccess,
          AttrezzatureActions.setAvailableSuccess,
          AttrezzatureActions.setAvailabilitySuccess,
          AttrezzatureActions.associateToCantiereSuccess,
          AttrezzatureActions.endCantiereAssociationSuccess,
          AttrezzatureActions.associateToFornitoreSuccess,
          AttrezzatureActions.endFornitoreAssociationSuccess,
          AttrezzatureActions.archiveSuccess,
        ),
        tap((action) => {
          switch (action.type) {
            case AttrezzatureActions.createSuccess.type:
              this.swalService.success('Attrezzatura creata con successo!');
              return;
            case AttrezzatureActions.updateSuccess.type:
              this.swalService.success('Attrezzatura aggiornata con successo!');
              return;
            case AttrezzatureActions.setAvailableSuccess.type:
              this.swalService.success('Attrezzatura impostata disponibile');
              return;
            case AttrezzatureActions.setAvailabilitySuccess.type:
              this.swalService.success('Disponibilità aggiornata con successo');
              return;
            case AttrezzatureActions.associateToCantiereSuccess.type:
              this.swalService.success('Attrezzatura associata al cantiere!');
              return;
            case AttrezzatureActions.endCantiereAssociationSuccess.type:
              this.swalService.success('Associazione cantiere terminata');
              return;
            case AttrezzatureActions.associateToFornitoreSuccess.type:
              this.swalService.success('Attrezzatura associata al fornitore!');
              return;
            case AttrezzatureActions.endFornitoreAssociationSuccess.type:
              this.swalService.success('Associazione fornitore terminata');
              return;
            case AttrezzatureActions.archiveSuccess.type:
              this.swalService.success('Operazione completata con successo');
              return;
          }
        })
      ),
    { dispatch: false }
  );

  showMutationFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          AttrezzatureActions.createFailure,
          AttrezzatureActions.updateFailure,
          AttrezzatureActions.setAvailableFailure,
          AttrezzatureActions.setAvailabilityFailure,
          AttrezzatureActions.associateToCantiereFailure,
          AttrezzatureActions.endCantiereAssociationFailure,
          AttrezzatureActions.associateToFornitoreFailure,
          AttrezzatureActions.endFornitoreAssociationFailure,
          AttrezzatureActions.archiveFailure,
        ),
        tap(({ error }) => this.swalService.error(`Errore: ${error}`))
      ),
    { dispatch: false }
  );

  showLoadFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AttrezzatureActions.loadListFailure, AttrezzatureActions.loadDetailsFailure),
        tap(({ error }) => this.swalService.apiError(error))
      ),
    { dispatch: false }
  );
}
